<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Plan</h1>
        <p class="console-page__placeholder">
          <span class="console-badge console-badge--small" :class="planStatusToneClass">{{ plan.status || 'unknown' }}</span>
          <span v-if="workspaceId" class="plan-detail__stat">workspace {{ workspaceId }}</span>
          <span v-if="jobDescription" class="plan-detail__stat">{{ summarizeJobTitle(jobDescription, jobId, 56) }}</span>
        </p>
      </div>

      <div class="console-page__actions">
        <router-link :to="{ name: 'ConsolePlans' }" class="button button--ghost">
          Back to Plans
        </router-link>
        <router-link v-if="jobId" :to="{ name: 'ConsoleJobDetail', params: { id: jobId } }" class="button button--ghost">
          Run
        </router-link>
      </div>
    </div>

    <section class="console-panel" :class="planStatusToneClass.replace('console-badge', 'console-panel')">
      <ConsoleListState
        :loading="loading"
        :error="error"
        :is-empty="false"
        loading-message="Loading plan..."
      >
        <div v-if="plan.status === 'pending_approval'" class="plan-detail__action-block">
          <p><strong>Decision required.</strong> Review the proposed steps and choose approve, reject, or modify.</p>
          <div class="button-row">
            <button type="button" class="button button--primary" :disabled="submitting" @click="approvePlan">
              {{ submitting && pendingDecision === 'approve' ? 'Approving...' : 'Approve' }}
            </button>
            <button type="button" class="button button--ghost" :disabled="submitting" @click="showRejectForm = !showRejectForm">
              Reject
            </button>
            <button type="button" class="button button--ghost" :disabled="submitting" @click="toggleModify">
              {{ modifying ? 'Cancel Modify' : 'Modify' }}
            </button>
          </div>

          <div v-if="showRejectForm" class="plan-detail__decision-shell">
            <label class="console-filter">
              <span class="console-filter__label">Rejection Reason</span>
              <textarea
                v-model="rejectReason"
                class="form-input plan-detail__textarea"
                rows="4"
                placeholder="Why should this plan be rejected?"
              />
            </label>
            <button type="button" class="button button--ghost" :disabled="submitting" @click="rejectPlan">
              {{ submitting && pendingDecision === 'reject' ? 'Rejecting...' : 'Confirm Reject' }}
            </button>
          </div>
        </div>

        <details class="console-module" open>
          <summary class="console-module__summary">Context</summary>
          <dl class="console-metric-list">
            <div class="console-metric">
              <dt>Plan ID</dt>
              <dd class="plan-detail__mono">{{ planId }}</dd>
            </div>
            <div class="console-metric">
              <dt>Workspace</dt>
              <dd>{{ workspaceId || 'Unknown' }}</dd>
            </div>
            <div class="console-metric">
              <dt>Run</dt>
              <dd>{{ jobId || 'Unknown' }}</dd>
            </div>
            <div class="console-metric">
              <dt>Status</dt>
              <dd>{{ formatMetricLabel(plan.status || 'unknown') }}</dd>
            </div>
          </dl>
        </details>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Proposed Steps
            <span class="console-module__count">{{ editableBeads.length }}</span>
          </summary>

          <div class="plan-detail__bead-list">
            <article v-for="(bead, index) in editableBeads" :key="`bead-${index}`" class="plan-detail__bead">
              <div class="plan-detail__bead-header">
                <span class="plan-detail__bead-index">#{{ index }}</span>
                <span v-if="bead.dependsOnLabel" class="plan-detail__bead-deps">depends on {{ bead.dependsOnLabel }}</span>
              </div>

              <template v-if="modifying">
                <label class="console-filter">
                  <span class="console-filter__label">Prompt</span>
                  <textarea v-model="bead.prompt" class="form-input plan-detail__textarea" rows="4" />
                </label>
                <label class="console-filter">
                  <span class="console-filter__label">Acceptance Criteria</span>
                  <textarea v-model="bead.acceptanceCriteria" class="form-input plan-detail__textarea" rows="3" placeholder="Optional" />
                </label>

                <div class="button-row plan-detail__bead-actions">
                  <button type="button" class="button button--ghost" :disabled="index === 0" @click="moveBead(index, -1)">Move Up</button>
                  <button type="button" class="button button--ghost" :disabled="index === editableBeads.length - 1" @click="moveBead(index, 1)">Move Down</button>
                  <button type="button" class="button button--ghost plan-detail__danger" @click="removeBead(index)">Remove</button>
                </div>
              </template>

              <template v-else>
                <div class="plan-detail__prose" v-html="renderMarkdown(bead.prompt)" />
                <div v-if="bead.acceptanceCriteria" class="plan-detail__acceptance">
                  <div class="plan-detail__acceptance-label">Acceptance Criteria</div>
                  <div class="plan-detail__prose" v-html="renderMarkdown(bead.acceptanceCriteria)" />
                </div>
              </template>
            </article>
          </div>

          <div v-if="modifying" class="button-row">
            <button type="button" class="button button--ghost" @click="addBead">Add Step</button>
            <button type="button" class="button button--primary" :disabled="submitting || !hasValidModifiedBeads" @click="submitModify">
              {{ submitting && pendingDecision === 'modify' ? 'Submitting...' : 'Submit Modified Plan' }}
            </button>
          </div>
        </details>

        <div v-if="submitError" class="alert alert--error">
          {{ submitError }}
        </div>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ConsoleListState from '@/components/ConsoleListState.vue'
import { summarizeJobTitle } from '@/composables/useJobPresentation.js'
import { renderMarkdown } from '@/composables/useMarkdown.js'
import { decodePlanId } from '@/composables/usePlanId.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { appWorkspaceTaskClient } from '@/services/WorkspaceTaskClient.js'

const route = useRoute()
const router = useRouter()
const { request, requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsolePlanDetail]')

const planId = route.params.id
const decodedPlanRef = decodePlanId(planId) ?? {}
const workspaceId = decodedPlanRef.workspaceId || ''
const jobId = decodedPlanRef.jobId || ''

const loading = ref(true)
const error = ref('')
const submitting = ref(false)
const submitError = ref('')
const pendingDecision = ref('')
const plan = ref({ status: 'pending_approval', proposedBeads: [] })
const jobDescription = ref('')
const showRejectForm = ref(false)
const rejectReason = ref('')
const modifying = ref(false)
const editableBeads = ref([])

const planStatusToneClass = computed(() => {
  const status = plan.value.status
  if (status === 'approved') return 'console-badge console-badge--ready'
  if (status === 'rejected' || status === 'auto_rejected') return 'console-badge console-badge--failed'
  if (status === 'modified') return 'console-badge console-badge--active'
  return 'console-badge console-badge--pending'
})

const hasValidModifiedBeads = computed(() => {
  return editableBeads.value.some((bead) => bead.prompt.trim())
})

function formatMetricLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .trim()
}

function normalizeBeads(beads) {
  return (Array.isArray(beads) ? beads : []).map((bead) => ({
    prompt: typeof bead.prompt === 'string' ? bead.prompt : '',
    acceptanceCriteria: typeof bead.acceptanceCriteria === 'string' ? bead.acceptanceCriteria : '',
    dependsOn: Array.isArray(bead.dependsOn) ? [...bead.dependsOn] : [],
    dependsOnLabel: Array.isArray(bead.dependsOn) && bead.dependsOn.length
      ? bead.dependsOn.map((index) => `#${index}`).join(', ')
      : '',
  }))
}

async function fetchPlan() {
  const data = await requestJson(`/plans/${planId}`)
  plan.value = {
    ...data,
    proposedBeads: Array.isArray(data.proposedBeads) ? data.proposedBeads : [],
  }
  editableBeads.value = normalizeBeads(plan.value.proposedBeads)
}

async function fetchJob() {
  if (!jobId) return

  try {
    const data = await requestJson(`/jobs/${jobId}`)
    jobDescription.value = data.description || ''
  } catch (fetchError) {
    jobDescription.value = ''
    logger.warn('Failed to fetch run context for plan detail', fetchError)
  }
}

async function loadData({ showLoading = false } = {}) {
  if (showLoading) loading.value = true
  error.value = ''
  submitError.value = ''

  try {
    await Promise.all([
      fetchPlan(),
      fetchJob(),
    ])
  } catch (loadError) {
    error.value = 'Failed to load plan.'
    logger.warn('Failed to fetch plan detail', loadError)
  } finally {
    loading.value = false
  }
}

function toggleModify() {
  modifying.value = !modifying.value
  showRejectForm.value = false
  if (!modifying.value) {
    editableBeads.value = normalizeBeads(plan.value.proposedBeads)
  }
}

function moveBead(index, direction) {
  const targetIndex = index + direction
  const next = [...editableBeads.value]
  const current = next[index]
  next[index] = next[targetIndex]
  next[targetIndex] = current
  editableBeads.value = next
}

function removeBead(index) {
  editableBeads.value = editableBeads.value.filter((_, beadIndex) => beadIndex !== index)
}

function addBead() {
  editableBeads.value = [
    ...editableBeads.value,
    {
      prompt: '',
      acceptanceCriteria: '',
      dependsOn: [],
      dependsOnLabel: '',
    },
  ]
}

async function approvePlan() {
  if (!workspaceId) return

  submitting.value = true
  pendingDecision.value = 'approve'
  submitError.value = ''

  try {
    const response = await request(`/plans/${planId}/decide`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ decision: 'approve' }),
    })

    if (response.status === 409 && response.data?.stale) {
      await router.push({
        name: 'ConsoleWorkspaceDetail',
        params: { id: response.data.workspaceId || workspaceId },
        query: {
          reason: 'stale-approval',
          source: 'plan',
          jobId,
        },
      })
      return
    }

    const result = await appWorkspaceTaskClient.resolveTaskResponse({
      request,
      workspaceId,
      response,
    })

    if (result?.stale && (result.workspaceId || workspaceId)) {
      await router.push({
        name: 'ConsoleWorkspaceDetail',
        params: { id: result.workspaceId || workspaceId },
        query: {
          reason: 'stale-approval',
          source: 'plan',
          jobId,
        },
      })
      return
    }

    await router.push({ name: 'ConsoleJobDetail', params: { id: jobId } })
  } catch (decisionError) {
    submitError.value = decisionError.message || 'Failed to approve plan.'
    logger.warn('Failed to approve plan', decisionError)
  } finally {
    submitting.value = false
    pendingDecision.value = ''
  }
}

async function rejectPlan() {
  submitting.value = true
  pendingDecision.value = 'reject'
  submitError.value = ''

  try {
    await requestJson(`/plans/${planId}/decide`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        decision: 'reject',
        ...(rejectReason.value.trim() ? { reason: rejectReason.value.trim() } : {}),
      }),
    })
    await router.push({ name: 'ConsolePlans' })
  } catch (decisionError) {
    submitError.value = decisionError.message || 'Failed to reject plan.'
    logger.warn('Failed to reject plan', decisionError)
  } finally {
    submitting.value = false
    pendingDecision.value = ''
  }
}

async function submitModify() {
  submitting.value = true
  pendingDecision.value = 'modify'
  submitError.value = ''

  try {
    const modifiedBeads = editableBeads.value
      .filter((bead) => bead.prompt.trim())
      .map((bead) => ({
        prompt: bead.prompt.trim(),
        ...(bead.acceptanceCriteria.trim() ? { acceptanceCriteria: bead.acceptanceCriteria.trim() } : {}),
      }))

    await requestJson(`/plans/${planId}/decide`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        decision: 'modify',
        modifiedBeads,
      }),
    })

    await router.push({ name: 'ConsolePlans' })
  } catch (decisionError) {
    submitError.value = decisionError.message || 'Failed to submit modified plan.'
    logger.warn('Failed to modify plan', decisionError)
  } finally {
    submitting.value = false
    pendingDecision.value = ''
  }
}

onMounted(() => {
  loadData({ showLoading: true })
})
</script>

<style scoped>
.plan-detail__stat {
  margin-left: 12px;
  color: var(--shell-text-muted);
}

.plan-detail__mono {
  font-family: var(--shell-mono-font);
  font-size: 0.78rem;
}

.plan-detail__action-block {
  padding: 20px 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.04) 100%);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.plan-detail__action-block p {
  margin: 0 0 16px;
  font-size: 0.95rem;
  color: var(--shell-text);
}

.plan-detail__action-block strong {
  color: #b45309;
}

.plan-detail__decision-shell {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

.plan-detail__bead-list {
  display: grid;
  gap: 12px;
}

.plan-detail__bead {
  padding: 14px 16px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface-strong);
}

.plan-detail__bead-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.plan-detail__bead-index {
  font-family: var(--shell-mono-font);
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--shell-accent);
}

.plan-detail__bead-deps {
  font-size: 0.76rem;
  color: var(--shell-text-muted);
}

.plan-detail__prose {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.65;
  color: var(--shell-text);
}

.plan-detail__prose :deep(*:first-child) {
  margin-top: 0;
}

.plan-detail__prose :deep(*:last-child) {
  margin-bottom: 0;
}

.plan-detail__acceptance {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--shell-border);
}

.plan-detail__acceptance-label {
  margin-bottom: 8px;
  font-family: var(--shell-mono-font);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--shell-text-muted);
}

.plan-detail__textarea {
  resize: vertical;
  line-height: 1.6;
}

.plan-detail__bead-actions {
  margin-top: 12px;
}

.plan-detail__danger {
  border-color: rgba(156, 48, 48, 0.24);
  color: #9c3030;
}
</style>
